import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Skeleton, Spinner, Tabs, useOverlayState } from '@heroui/react';
import {
  approveSpotUsage,
  cancelAvailabilityWindow,
  cancelAvailabilityWindowSeries,
  cancelSpotUsage,
  createAvailabilityWindow,
  finishSpotUsage,
  reclaimSpotUsage,
  getAvailabilityWindowsBySpot,
  getPendingRequests,
  getSpotUsages,
  myParkingSpotsKeys,
  rejectSpotUsage,
  updateAvailabilityWindow,
  type AvailabilityWindow,
  type CreateAvailabilityWindowRequest,
  type ParkingSpotOwner,
  type UpdateAvailabilityWindowRequest,
} from '../../../api/my-parking-spots';
import { PageHeader } from '../../../components/page/page-header';
import { FloorPlanDrawer } from '../../../components/floor-plan/floor-plan-drawer';
import { floorPlanRectFromLocation } from '../../../components/floor-plan/floor-plan-utils';
import { primaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import { TabCountBadge } from '../../../components/ui/tab-count-badge';
import { StatusCallout } from '../../../components/ui/status-callout';
import { useTenantParkingSpots } from '../../../hooks/useTenantParkingSpots';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { invalidateParkingLists } from '../../../lib/parking-query';
import { ActiveWindowsDrawer } from '../components/active-windows-drawer';
import { AvailabilityWindowDrawer } from '../components/availability-window-drawer';
import { BorrowedUsagesSection } from '../components/borrowed-usages-section';
import { ConfirmCancelWindowModal } from '../components/confirm-cancel-window-modal';
import { ConfirmUsageModal, type UsageConfirmAction } from '../components/confirm-usage-modal';
import { OwnedSpotsSection } from '../components/owned-spots-section';
import { PendingRequestsDrawer } from '../components/pending-requests-drawer';
import { SpotUsagesDrawer } from '../components/spot-usages-drawer';
import { filterAndSortActiveWindows } from '../utils/availability-windows';
import { calculateDynamicStatus } from '../utils/calculate-dynamic-status';

export default function MyParkingSpotsPage() {
  const queryClient = useQueryClient();
  const {
    condominiumId,
    condominiumLabel,
    spots,
    borrowedUsages: borrowedSpots,
    isLoading: isTenantLoading,
    isError: isTenantError,
    refetch: refetchTenantSpots,
  } = useTenantParkingSpots();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<string>('owned');

  const windowDrawer = useOverlayState();
  const activeWindowsDrawer = useOverlayState();
  const pendingDrawer = useOverlayState();
  const usagesDrawer = useOverlayState();
  const confirmModal = useOverlayState();
  const confirmCancelWindowModal = useOverlayState();
  const locationDrawer = useOverlayState();

  const [selectedSpot, setSelectedSpot] = useState<ParkingSpotOwner | null>(null);
  const [selectedSpotForActiveWindows, setSelectedSpotForActiveWindows] =
    useState<ParkingSpotOwner | null>(null);
  const [selectedSpotForPending, setSelectedSpotForPending] = useState<ParkingSpotOwner | null>(null);
  const [selectedSpotForUsages, setSelectedSpotForUsages] = useState<ParkingSpotOwner | null>(null);
  const [selectedSpotForLocation, setSelectedSpotForLocation] = useState<ParkingSpotOwner | null>(
    null,
  );
  const [editingWindow, setEditingWindow] = useState<AvailabilityWindow | null>(null);
  const [highlightedUsageId, setHighlightedUsageId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<UsageConfirmAction | null>(null);
  const [confirmUsageId, setConfirmUsageId] = useState<string | null>(null);
  const [windowToCancel, setWindowToCancel] = useState<AvailabilityWindow | null>(null);
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  useEffect(() => {
    setSelectedTab('owned');
  }, [condominiumId]);

  const spotMetaQueries = useQueries({
    queries: spots.flatMap((spot) => [
      {
        queryKey: myParkingSpotsKeys.windows(spot.spotId),
        queryFn: () => getAvailabilityWindowsBySpot(spot.spotId),
        select: filterAndSortActiveWindows,
      },
      {
        queryKey: myParkingSpotsKeys.usages(spot.spotId),
        queryFn: () => getSpotUsages(spot.spotId),
        select: (usages: Awaited<ReturnType<typeof getSpotUsages>>) =>
          usages.filter((u) => u.status === 'ACTIVE'),
      },
      {
        queryKey: myParkingSpotsKeys.pending(spot.spotId),
        queryFn: () => getPendingRequests(spot.spotId),
      },
    ]),
  });

  const spotMeta = useMemo(() => {
    const windowsBySpot = new Map<string, AvailabilityWindow[]>();
    const activeUsagesBySpot = new Map<string, Awaited<ReturnType<typeof getSpotUsages>>>();
    const pendingBySpot = new Map<string, Awaited<ReturnType<typeof getPendingRequests>>>();

    spots.forEach((spot, index) => {
      const base = index * 3;
      windowsBySpot.set(spot.spotId, spotMetaQueries[base]?.data ?? []);
      activeUsagesBySpot.set(spot.spotId, spotMetaQueries[base + 1]?.data ?? []);
      pendingBySpot.set(spot.spotId, spotMetaQueries[base + 2]?.data ?? []);
    });

    return { windowsBySpot, activeUsagesBySpot, pendingBySpot };
  }, [spots, spotMetaQueries]);

  const ownedRows = useMemo(
    () =>
      spots.map((spot) => {
        const windows = spotMeta.windowsBySpot.get(spot.spotId) ?? [];
        const activeUsages = spotMeta.activeUsagesBySpot.get(spot.spotId) ?? [];
        const pending = spotMeta.pendingBySpot.get(spot.spotId) ?? [];
        return {
          spot,
          status: calculateDynamicStatus(spot, windows, activeUsages),
          pendingCount: pending.length,
          activeWindowsCount: windows.length,
          activeUsage: activeUsages[0] ?? null,
        };
      }),
    [spots, spotMeta],
  );

  const activeWindowsSpotId = selectedSpotForActiveWindows?.spotId;

  const activeWindowsQuery = useQuery({
    queryKey: myParkingSpotsKeys.windows(activeWindowsSpotId ?? 'none'),
    queryFn: () => getAvailabilityWindowsBySpot(activeWindowsSpotId!),
    enabled: Boolean(activeWindowsSpotId && activeWindowsDrawer.isOpen),
    select: filterAndSortActiveWindows,
  });

  const windowDrawerSpotId = selectedSpot?.spotId;

  const windowDrawerWindowsQuery = useQuery({
    queryKey: myParkingSpotsKeys.windows(windowDrawerSpotId ?? 'none'),
    queryFn: () => getAvailabilityWindowsBySpot(windowDrawerSpotId!),
    enabled: Boolean(windowDrawerSpotId && windowDrawer.isOpen),
    select: filterAndSortActiveWindows,
  });

  const usagesSpotId = selectedSpotForUsages?.spotId;
  const spotUsagesQuery = useQuery({
    queryKey: [...myParkingSpotsKeys.usages(usagesSpotId ?? 'none'), 'history'],
    queryFn: () => getSpotUsages(usagesSpotId!),
    enabled: Boolean(usagesSpotId && usagesDrawer.isOpen),
  });

  const invalidateSpot = async () => {
    await invalidateParkingLists(queryClient);
  };

  const createWindowMutation = useMutation({
    mutationFn: createAvailabilityWindow,
    onSuccess: async (data) => {
      if (data.recurrence) {
        const skipped =
          data.recurrence.skippedCount > 0
            ? ` ${data.recurrence.skippedCount} pulada(s) por conflito.`
            : '';
        showSuccess(`${data.recurrence.createdCount} janelas criadas.${skipped}`);
      } else {
        showSuccess('Janela de disponibilidade criada.');
      }
      await invalidateSpot();
    },
  });

  const updateWindowMutation = useMutation({
    mutationFn: ({ windowId, payload }: { windowId: string; payload: UpdateAvailabilityWindowRequest }) =>
      updateAvailabilityWindow(windowId, payload),
    onSuccess: async () => {
      showSuccess('Janela atualizada.');
      await invalidateSpot();
    },
  });

  const cancelWindowMutation = useMutation({
    mutationFn: cancelAvailabilityWindow,
    onSuccess: async () => {
      showSuccess('Janela cancelada.');
      await invalidateSpot();
    },
    onError: showErrorFromUnknown,
  });

  const cancelSeriesMutation = useMutation({
    mutationFn: cancelAvailabilityWindowSeries,
    onSuccess: async (data) => {
      showSuccess(
        data.cancelledCount > 1
          ? `${data.cancelledCount} janelas da série foram canceladas.`
          : 'Série recorrente cancelada.',
      );
      await invalidateSpot();
    },
    onError: showErrorFromUnknown,
  });

  const approveMutation = useMutation({
    mutationFn: approveSpotUsage,
    onSuccess: async () => {
      showSuccess('Solicitação aprovada.');
      await invalidateSpot();
    },
    onError: showErrorFromUnknown,
  });

  const rejectMutation = useMutation({
    mutationFn: rejectSpotUsage,
    onSuccess: async () => {
      showSuccess('Solicitação rejeitada.');
      await invalidateSpot();
    },
    onError: showErrorFromUnknown,
  });

  const cancelUsageMutation = useMutation({
    mutationFn: cancelSpotUsage,
    onSuccess: async () => {
      showSuccess('Uso cancelado. O proprietário foi notificado.');
      await invalidateSpot();
    },
    onError: showErrorFromUnknown,
  });

  const finishUsageMutation = useMutation({
    mutationFn: finishSpotUsage,
    onSuccess: async () => {
      showSuccess('Vaga liberada. O proprietário foi notificado.');
      await invalidateSpot();
    },
    onError: showErrorFromUnknown,
  });

  const reclaimUsageMutation = useMutation({
    mutationFn: reclaimSpotUsage,
    onSuccess: async () => {
      showSuccess('Vaga retomada. O morador foi notificado.');
      await invalidateSpot();
    },
    onError: showErrorFromUnknown,
  });

  useEffect(() => {
    if (deepLinkHandled || spots.length === 0) return;

    const spotIdFromUrl = searchParams.get('spotId');
    const openPendingRequests = searchParams.get('openPendingRequests');
    const openAvailabilityParam = searchParams.get('openAvailability') === 'true';
    const usageIdFromUrl = searchParams.get('usageId');
    if (!spotIdFromUrl) return;

    const spot = spots.find((s) => s.spotId === spotIdFromUrl);
    if (!spot) return;

    setSelectedTab('owned');

    if (openAvailabilityParam) {
      setSelectedSpot(spot);
      setEditingWindow(null);
      void queryClient
        .ensureQueryData({
          queryKey: myParkingSpotsKeys.windows(spot.spotId),
          queryFn: () => getAvailabilityWindowsBySpot(spot.spotId),
        })
        .then(() => windowDrawer.open());
      setSearchParams({}, { replace: true });
      setDeepLinkHandled(true);
      return;
    }

    setSelectedSpotForPending(spot);
    setSelectedTab('owned');

    if (usageIdFromUrl) {
      setHighlightedUsageId(usageIdFromUrl);
      setTimeout(() => setHighlightedUsageId(null), 5000);
    }

    if (openPendingRequests === 'true') {
      queryClient
        .fetchQuery({
          queryKey: myParkingSpotsKeys.pending(spot.spotId),
          queryFn: () => getPendingRequests(spot.spotId),
        })
        .then(() => pendingDrawer.open());
    } else {
      pendingDrawer.open();
    }

    setSearchParams({}, { replace: true });
    setDeepLinkHandled(true);
  }, [spots, searchParams, deepLinkHandled, setSearchParams, queryClient, pendingDrawer, windowDrawer]);

  const openAvailability = async (spot: ParkingSpotOwner) => {
    setSelectedSpot(spot);
    setEditingWindow(null);
    await queryClient.ensureQueryData({
      queryKey: myParkingSpotsKeys.windows(spot.spotId),
      queryFn: () => getAvailabilityWindowsBySpot(spot.spotId),
    });
    windowDrawer.open();
  };

  const openActiveWindows = (spot: ParkingSpotOwner) => {
    setSelectedSpotForActiveWindows(spot);
    activeWindowsDrawer.open();
  };

  const openEditWindow = async (window: AvailabilityWindow) => {
    const spot =
      selectedSpotForActiveWindows ??
      spots.find((s) => s.spotId === window.spotId) ??
      null;
    if (!spot) return;
    setSelectedSpot(spot);
    setEditingWindow(window);
    await queryClient.ensureQueryData({
      queryKey: myParkingSpotsKeys.windows(spot.spotId),
      queryFn: () => getAvailabilityWindowsBySpot(spot.spotId),
    });
    activeWindowsDrawer.close();
    windowDrawer.open();
  };

  const openPending = (spot: ParkingSpotOwner) => {
    setSelectedSpotForPending(spot);
    pendingDrawer.open();
  };

  const openUsages = (spot: ParkingSpotOwner) => {
    setSelectedSpotForUsages(spot);
    usagesDrawer.open();
  };

  const openLocation = (spot: ParkingSpotOwner) => {
    setSelectedSpotForLocation(spot);
    locationDrawer.open();
  };

  const openConfirmUsage = (usageId: string, action: UsageConfirmAction) => {
    setConfirmUsageId(usageId);
    setConfirmAction(action);
    confirmModal.open();
  };

  const openConfirmCancelWindow = (window: AvailabilityWindow) => {
    setWindowToCancel(window);
    confirmCancelWindowModal.open();
  };

  const handleConfirmCancelWindow = async () => {
    if (!windowToCancel) return;
    await cancelWindowMutation.mutateAsync(windowToCancel.id);
    setWindowToCancel(null);
  };

  const handleConfirmCancelSeries = async () => {
    if (!windowToCancel?.seriesId) return;
    await cancelSeriesMutation.mutateAsync(windowToCancel.seriesId);
    setWindowToCancel(null);
  };

  const handleConfirmUsage = async () => {
    if (!confirmUsageId || !confirmAction) return;
    if (confirmAction === 'cancel') {
      await cancelUsageMutation.mutateAsync(confirmUsageId);
    } else if (confirmAction === 'reclaim') {
      await reclaimUsageMutation.mutateAsync(confirmUsageId);
    } else {
      await finishUsageMutation.mutateAsync(confirmUsageId);
    }
    setConfirmUsageId(null);
    setConfirmAction(null);
  };

  const handleWindowSubmit = async (
    payload: CreateAvailabilityWindowRequest | { windowId: string; payload: UpdateAvailabilityWindowRequest },
  ) => {
    if ('windowId' in payload) {
      await updateWindowMutation.mutateAsync(payload);
    } else {
      await createWindowMutation.mutateAsync(payload);
    }
  };

  useEffect(() => {
    if (!windowDrawer.isOpen) {
      setEditingWindow(null);
    }
  }, [windowDrawer.isOpen]);

  const metaLoading = spotMetaQueries.some((q) => q.isLoading);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Minhas vagas"
        description={
          condominiumLabel
            ? `Disponibilidade e usos das suas vagas em ${condominiumLabel}.`
            : 'Gerencie disponibilidade, solicitações e usos das suas vagas.'
        }
      />

      {isTenantLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full max-w-md rounded-lg" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      ) : isTenantError ? (
        <StatusCallout
          status="danger"
          title="Erro ao carregar vagas"
          action={
            <Button size="sm" variant="secondary" onPress={() => void refetchTenantSpots()}>
              Tentar novamente
            </Button>
          }
        >
          Tente novamente em instantes.
        </StatusCallout>
      ) : !condominiumId ? (
        <StatusCallout status="warning" title="Condomínio não selecionado">
          Selecione um condomínio no menu do usuário para ver suas vagas.
        </StatusCallout>
      ) : (
        <Tabs selectedKey={selectedTab} onSelectionChange={(key) => setSelectedTab(String(key))}>
          <Tabs.ListContainer>
            <Tabs.List aria-label="Minhas vagas" className="p-1.5" data-tour="my-spots-tabs">
              <Tabs.Tab id="owned" className={primaryPageTabClassName}>
                <span className="inline-flex items-center gap-2">
                  Minhas
                  <TabCountBadge count={spots.length} />
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id="borrowed" className={primaryPageTabClassName}>
                <span className="inline-flex items-center gap-2">
                  Usando
                  <TabCountBadge count={borrowedSpots.length} />
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>

          <Tabs.Panel id="owned" className="pt-3">
            {metaLoading && spots.length > 0 ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <OwnedSpotsSection
                rows={ownedRows}
                onAvailability={openAvailability}
                onActiveWindows={openActiveWindows}
                onPending={openPending}
                onUsages={openUsages}
                onViewLocation={openLocation}
                onReclaim={(id) => openConfirmUsage(id, 'reclaim')}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel id="borrowed" className="pt-3">
            {isTenantLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <BorrowedUsagesSection
                usages={borrowedSpots}
                onCancel={(id) => openConfirmUsage(id, 'cancel')}
                onFinish={(id) => openConfirmUsage(id, 'finish')}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      )}

      <AvailabilityWindowDrawer
        state={windowDrawer}
        spot={selectedSpot}
        activeWindows={
          windowDrawerWindowsQuery.data ??
          (selectedSpot ? (spotMeta.windowsBySpot.get(selectedSpot.spotId) ?? []) : [])
        }
        editingWindow={editingWindow}
        onSubmit={handleWindowSubmit}
      />

      <ActiveWindowsDrawer
        state={activeWindowsDrawer}
        spot={selectedSpotForActiveWindows}
        windows={activeWindowsQuery.data ?? []}
        loading={activeWindowsQuery.isLoading}
        onEditWindow={openEditWindow}
        onCancelWindow={openConfirmCancelWindow}
      />

      <PendingRequestsDrawer
        state={pendingDrawer}
        spot={selectedSpotForPending}
        requests={
          selectedSpotForPending
            ? (spotMeta.pendingBySpot.get(selectedSpotForPending.spotId) ?? [])
            : []
        }
        highlightedUsageId={highlightedUsageId}
        onApprove={(id) => approveMutation.mutateAsync(id)}
        onReject={(id) => rejectMutation.mutateAsync(id)}
      />

      <SpotUsagesDrawer
        state={usagesDrawer}
        spot={selectedSpotForUsages}
        usages={spotUsagesQuery.data ?? []}
        loading={spotUsagesQuery.isLoading}
        error={spotUsagesQuery.isError ? 'Erro ao carregar histórico.' : null}
        onReclaimUsage={(id) => openConfirmUsage(id, 'reclaim')}
      />

      <ConfirmUsageModal
        state={confirmModal}
        action={confirmAction}
        onConfirm={handleConfirmUsage}
      />

      <ConfirmCancelWindowModal
        state={confirmCancelWindowModal}
        window={windowToCancel}
        onConfirm={handleConfirmCancelWindow}
        onConfirmSeries={handleConfirmCancelSeries}
      />

      <FloorPlanDrawer
        state={locationDrawer}
        title={
          selectedSpotForLocation
            ? `Localização da vaga ${selectedSpotForLocation.spotCode}`
            : 'Localização da vaga'
        }
        subtitle="Visualização da vaga na planta do condomínio"
        rect={
          selectedSpotForLocation ? floorPlanRectFromLocation(selectedSpotForLocation) : null
        }
      />
    </div>
  );
}
